<template>
  <div
    data-testid="sf-markdown-mention-list"
    :class="theme.dropdownBorder"
    class="
      tw-z-mentions
      tw-rounded-lg
      tw-border
      tw-border-solid
      tw-max-h-60
      tw-max-w-[560px]
      tw-overflow-hidden
      tw-m-0
      tw-bg-white
    "
    @keydown="handleDOMKeyDown"
  >
    <div
      v-if="isLoading"
      class="tw-px-4 tw-py-8 tw-text-center tw-text-sf-neutral-light-1"
    >
      <div class="sf-spinner sf-inline-block" data-testid="sf-spinner"></div>
    </div>
    <SfList
      v-else-if="items.length > 0"
      class="tw-overflow-y-auto tw-max-h-60 tw-m-0"
    >
      <SfListItem
        v-for="(item, index) in items"
        :key="item.id"
        v-scroll-into-view="
          index === selectedIndex ? selectedScrollOptions : false
        "
        :variant="'lg'"
        :item-id="item.id"
        :selected="index === selectedIndex"
        @item-click="selectItem(index)"
      >
        {{ item.id }}
      </SfListItem>
    </SfList>
    <div
      v-else
      class="tw-px-4 tw-py-8 tw-text-center tw-text-sf-neutral-light-1"
    >
      {{
        I18n.t('components.markdown_editor.document_link_picker.no_items_found')
      }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { commsTheme } from '@app/shared/comms-theme'

const theme = commsTheme()

import { ref, shallowRef, watch } from 'vue'

import SfList from '@app/shared/vue/components/sf-list/sf-list.vue'
import { SfListItem } from '@silverfin/ui-components'
import useI18n from '@app/shared/vue/composables/use-I18n'
import scrollIntoViewDirective from '@app/shared/vue/directives/scrollIntoView'
import type { MentionItem } from './types'

const I18n = useI18n()

// Required by TipTap's suggestion plugin: the VueRenderer calls `ref.onKeyDown()`
// on the component instance to forward keyboard events from the editor.
defineExpose({
  onKeyDown,
})

const vScrollIntoView = scrollIntoViewDirective
// Each navigation assigns a new object so the v-scroll-into-view directive always
// detects a reference change and triggers scrollIntoView — even when selectedIndex
// doesn't change (e.g. wrap-around back to the same position after manual scrolling).
const selectedScrollOptions = shallowRef<{ block: 'nearest' } | false>(false)

const props =
  defineProps<{
    items: MentionItem[]
    command: (payload: {
      id: string
      mentionType: string
      link?: string
    }) => void
    isLoading?: boolean
  }>()

const selectedIndex = ref(0)

watch(
  () => props.items,
  () => {
    selectedIndex.value = 0
    selectedScrollOptions.value = { block: 'nearest' }
  }
)

function upHandler(): void {
  const length = props.items.length
  if (length > 0) {
    selectedIndex.value = (selectedIndex.value + length - 1) % length
    selectedScrollOptions.value = { block: 'nearest' }
  }
}

function downHandler(): void {
  const length = props.items.length
  if (length > 0) {
    selectedIndex.value = (selectedIndex.value + 1) % length
    selectedScrollOptions.value = { block: 'nearest' }
  }
}

function enterHandler(): void {
  selectItem(selectedIndex.value)
}

function selectItem(index: number): void {
  const item = props.items[index]
  if (item) {
    props.command({ id: item.id, mentionType: item.type, link: item.link })
  }
}

function handleKeyDown(event: KeyboardEvent): boolean {
  switch (event.key) {
    case 'ArrowUp':
      upHandler()
      return true
    case 'ArrowDown':
      downHandler()
      return true
    case 'Enter':
      enterHandler()
      return true
    default:
      return false
  }
}

// For TipTap suggestion plugin
function onKeyDown({ event }: { event: KeyboardEvent }): boolean {
  return handleKeyDown(event)
}

// For direct DOM keydown events (when focus is inside the dropdown)
function handleDOMKeyDown(event: KeyboardEvent): void {
  if (handleKeyDown(event)) {
    event.preventDefault()
  }
}
</script>
